// src/components/common/NotificationBell.js
// Cloche de notifications client (acheteur) : badge + panneau déroulant.
// Deep-link : message -> /messages?c=<id>, commande -> /dashboard/orders.
// RTL/Arabe : police Cairo héritée de body.rtl, alignement via dir.
// Maxula

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiShoppingBag, FiMessageCircle, FiInbox } from 'react-icons/fi';
import { notifAPI } from '../../services/api';

const BRAND = '#e11d7b';

// Libellés bilingues (FR par défaut, AR pour les surfaces RTL).
const L = {
  fr: { title: 'Notifications', empty: 'Aucune notification', markAll: 'Tout marquer comme lu', now: "à l'instant", min: 'min', h: 'h', d: 'j' },
  ar: { title: 'الإشعارات', empty: 'لا توجد إشعارات', markAll: 'تحديد الكل كمقروء', now: 'الآن', min: 'د', h: 'س', d: 'ي' }
};

const iconFor = (type) => {
  if (type === 'order') return FiShoppingBag;
  if (type === 'message') return FiMessageCircle;
  return FiBell;
};

export default function NotificationBell({ lang = 'fr', isRTL = false }) {
  const navigate = useNavigate();
  const tt = L[lang] || L.fr;
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const mounted = useRef(true);

  const timeAgo = (d) => {
    const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
    if (s < 60) return tt.now;
    const m = Math.floor(s / 60); if (m < 60) return `${m} ${tt.min}`;
    const h = Math.floor(m / 60); if (h < 24) return `${h} ${tt.h}`;
    return `${Math.floor(h / 24)} ${tt.d}`;
  };

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await notifAPI.unreadCount();
      if (mounted.current) setCount(Number(data?.count) || 0);
    } catch (e) {}
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await notifAPI.list({ limit: 12 });
      if (mounted.current) setItems(data?.data || []);
    } catch (e) {} finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  // Badge : polling léger toutes les 60s.
  useEffect(() => {
    mounted.current = true;
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { mounted.current = false; clearInterval(t); };
  }, [fetchCount]);

  // Fermeture au clic extérieur / Échap.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchList();
  };

  const linkFor = (n) => {
    const d = n.data || {};
    if (n.type === 'message') return d.conversationId ? `/messages?c=${d.conversationId}` : '/messages';
    if (n.type === 'order') return '/dashboard/orders';
    return '/dashboard';
  };

  const openItem = (n) => {
    setOpen(false);
    if (!n.read) {
      setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setCount((c) => Math.max(0, c - 1));
      notifAPI.markRead(n.id).catch(() => {});
    }
    navigate(linkFor(n));
  };

  const markAll = async () => {
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
    setCount(0);
    try { await notifAPI.markAllRead(); } catch (e) {}
  };

  const hasUnread = items.some((n) => !n.read);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        aria-label={tt.title}
        aria-expanded={open}
        className="relative p-2 text-gray-700 hover:text-[#e11d7b]"
      >
        <FiBell size={22} />
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white rounded-full latin-nums"
            style={{ backgroundColor: BRAND }}
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          dir={isRTL ? 'rtl' : 'ltr'}
          className={`absolute mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 ${isRTL ? 'left-0' : 'right-0'}`}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="font-semibold text-gray-900">{tt.title}</span>
            {hasUnread && (
              <button type="button" onClick={markAll} className="text-xs font-medium hover:underline" style={{ color: BRAND }}>
                {tt.markAll}
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2" style={{ borderColor: BRAND }} />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10 text-gray-400 text-sm">
                <FiInbox size={26} className="text-gray-300" />
                {tt.empty}
              </div>
            ) : (
              items.map((n) => {
                const Icon = iconFor(n.type);
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => openItem(n)}
                    className={`w-full flex gap-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${isRTL ? 'text-right' : 'text-left'} ${n.read ? '' : 'bg-pink-50/40'}`}
                  >
                    <span className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FCE7F1', color: BRAND }}>
                      <Icon size={16} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className={`truncate text-sm ${n.read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>{n.title || tt.title}</span>
                        <span className="text-[10px] text-gray-400 shrink-0 latin-nums">{timeAgo(n.createdAt)}</span>
                      </span>
                      {n.message && <span className="block text-xs text-gray-500 truncate mt-0.5">{n.message}</span>}
                    </span>
                    {!n.read && <span className="shrink-0 w-2 h-2 rounded-full mt-1.5" style={{ backgroundColor: BRAND }} />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
